import React, { MouseEventHandler, ReactNode, forwardRef, useEffect, useRef, useState } from "react";

import './bt-button.css';

type Props = {
  leftIcon?: ReactNode,
  rightIcon?: ReactNode,
  align?: string,
  color?: string,
  class?: string,
  type?: string,
  size?: string,
  cy?: any,
  disabled?: boolean,
  loaderShow?: boolean,
  style?: React.CSSProperties,
  onClick?: (MouseEventHandler<HTMLDivElement>),
  children?: ReactNode,
  holdToConfirm?: boolean,
  holdDuration?: number,
  ariaLabel?: string,
  title?: string,
  tabIndex?: number,
}

export default forwardRef(function BtButton({children, ...props}: Props, ref: React.ForwardedRef<HTMLDivElement>) {
  const { leftIcon, rightIcon, align='right', color='', class: varClass, size='base', cy, holdToConfirm, holdDuration = 2000 } = props;
  const [holdProgress, setHoldProgress] = useState(0);
  const holdProgressRef = useRef(0);
  const isLoadingOrDisabled = props.disabled || props.loaderShow;
  const rafRef = useRef<number | null>(null);
  const holdStartRef = useRef<number | null>(null);

  const setProgress = (v: number) => {
    holdProgressRef.current = v;
    setHoldProgress(v);
  };

  useEffect(() => {
    if (props.loaderShow) {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      setProgress(0);
    }
  }, [props.loaderShow, props.disabled]);

  const stopHold = () => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    holdStartRef.current = null;
    const startProgress = holdProgressRef.current;
    if (startProgress <= 0 || props.loaderShow || props.disabled) return;
    const releaseTime = performance.now();
    const releaseDuration = 400;
    const animateBack = (now: number) => {
      const progress = Math.max(startProgress * (1 - (now - releaseTime) / releaseDuration), 0);
      setProgress(progress);
      if (progress > 0) {
        rafRef.current = requestAnimationFrame(animateBack);
      } else {
        rafRef.current = null;
      }
    };
    rafRef.current = requestAnimationFrame(animateBack);
  };

  const startHold = () => {
    if (isLoadingOrDisabled) return;
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    holdStartRef.current = performance.now();
    const tick = (now: number) => {
      const elapsed = now - (holdStartRef.current ?? now);
      const progress = Math.min(elapsed / holdDuration, 1);
      setProgress(progress);
      if (progress >= 1) {
        rafRef.current = null;
        holdStartRef.current = null;
        (props.onClick as any)?.();
      } else {
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  const holdHandlers = holdToConfirm ? {
    onMouseDown: startHold,
    onMouseUp: stopHold,
    onMouseLeave: stopHold,
    onTouchStart: startHold,
    onTouchEnd: stopHold,
  } : {};

  // A focusable div is only really a button once it answers the keys one does.
  const isFocusable = props.tabIndex !== undefined;
  const a11yProps = isFocusable ? {
    tabIndex: props.tabIndex,
    role: 'button',
    onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      if (isLoadingOrDisabled || holdToConfirm) return;

      event.preventDefault();
      // onClick is typed for the pointer; keyboard activation hands it the keyboard event instead.
      props.onClick?.(event as unknown as React.MouseEvent<HTMLDivElement>);
    },
  } : {};

  return (
    <div
      ref={ref}
      style={props.style}
      className={`bt-button bt-align-${align} ${size}BtButtonSize ${color} ${varClass} ${props.loaderShow ? 'loader-show' : ''} ${isLoadingOrDisabled ? 'disabled' : ''}`}
      onClick={holdToConfirm ? undefined : (props.onClick ?? (() => {}))}
      data-cy={cy}
      aria-label={props.ariaLabel}
      title={props.title}
      {...a11yProps}
      {...holdHandlers}
    >
      <div className={isLoadingOrDisabled ? 'disabled' : undefined}>
        <span className="slot">
          {holdToConfirm && (
            <span style={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: `${holdProgress * 100}%`,
              background: 'rgba(255,255,255,0.25)',
              pointerEvents: 'none',
            }} />
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {leftIcon}
            {children}
          </div>
          {rightIcon}
        </span>
      </div>
      <span className="loader">
        <span className="bounce1"></span>
        <span className="bounce2"></span>
        <span className="bounce3"></span>
      </span>
    </div>
  )
})
